import { useSyncExternalStore } from "react"

import { publicApiFetchJson } from "@/lib/api"
import { isTransientRouteError } from "@/lib/route-error-utils"

/**
 * Global "backend unreachable" flag (deploy window, outage). Query/mutation failures that look
 * transient (502+/network — `isTransientRouteError`) trigger a `/health` probe; only when the
 * probe ALSO fails does the flag flip, so a one-off blip on a single endpoint never blanks the
 * app. While down, `/health` is re-polled until it answers, then recovery listeners run
 * (query-client refetches everything) — no page reload involved.
 */

const POLL_MS = 3_000

let down = false
let probing = false
let pollTimer: ReturnType<typeof setTimeout> | undefined
const storeListeners = new Set<() => void>()
const recoveredListeners = new Set<() => void>()

function emit(): void {
	for (const listener of storeListeners) listener()
}

async function healthOk(): Promise<boolean> {
	try {
		await publicApiFetchJson<{ status: string }>("/health")
		return true
	} catch {
		return false
	}
}

function schedulePoll(): void {
	// Bare setTimeout (not window.setTimeout): this module also runs in the node test env.
	pollTimer = setTimeout(() => {
		void healthOk().then((ok) => {
			if (!down) return
			if (!ok) {
				schedulePoll()
				return
			}
			down = false
			emit()
			for (const listener of recoveredListeners) listener()
		})
	}, POLL_MS)
}

/** Call with any failed query/mutation error; no-op unless it smells like the API is unreachable. */
export function notifyTransientFailure(error: unknown): void {
	if (down || probing || !isTransientRouteError(error)) return
	probing = true
	void healthOk().then((ok) => {
		probing = false
		if (ok || down) return
		down = true
		emit()
		schedulePoll()
	})
}

/** Run `cb` on every unreachable → reachable transition (registration is permanent). */
export function onBackendRecovered(cb: () => void): void {
	recoveredListeners.add(cb)
}

function subscribe(cb: () => void): () => void {
	storeListeners.add(cb)
	return () => storeListeners.delete(cb)
}

export function useBackendUnreachable(): boolean {
	return useSyncExternalStore(subscribe, () => down, () => false)
}

/** Non-hook read of the flag (tests, imperative call sites). */
export function isBackendUnreachable(): boolean {
	return down
}

/** Test-only: reset module-level state between cases. */
export function _resetBackendStatusForTests(): void {
	down = false
	probing = false
	clearTimeout(pollTimer)
	storeListeners.clear()
	recoveredListeners.clear()
}
